import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/store";
import { useColWidths } from "@/hooks/useColWidths";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Check, X, Eraser, Save, Send, Loader2, Grid3X3, FunctionSquare,
  MousePointerClick, History, Plus, Minus, ArrowRight, AlertTriangle, CheckCircle2,
  Lock, Unlock, Pencil, Eye, Search, Replace, RowsIcon, ColumnsIcon,
  Trash2, ArrowDown, ArrowUp, ArrowLeft, ArrowRightIcon,
  AlignLeft, AlignCenter, AlignRight
} from "lucide-react";
import { toast } from "sonner";
import { t } from "@/i18n/bg";
import { useAppStore } from "@/store";

function getColLetter(col) {
  let s = "", c = col;
  while (c >= 0) { s = String.fromCharCode((c % 26) + 65) + s; c = Math.floor(c / 26) - 1; }
  return s;
}
function cellToRef(row, col) { return `${getColLetter(col - 1)}${row}`; }
function colFromLetter(letter) {
  let col = 0;
  for (let i = 0; i < letter.length; i++) col = col * 26 + (letter.charCodeAt(i) - 64);
  return col;
}

const EXTRA_ROWS = 5;
const EXTRA_COLS = 2;

export default function VisualInputMapEditor({ templates }) {
  const { units, fetchUnits } = useAppStore();
  const [templateId, setTemplateId] = useState(null);
  const [templateSheets, setTemplateSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(null);
  const [cells, setCells] = useState([]);
  const [inputMap, setInputMap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // Editor mode: "edit" = click to edit cells, "inputmap" = click to toggle editable, "rowvis" = row visibility
  const [editorMode, setEditorMode] = useState("edit");
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [localOverrides, setLocalOverrides] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [diff, setDiff] = useState(null);
  const [publishResult, setPublishResult] = useState(null);
  const [showPublishResult, setShowPublishResult] = useState(false);
  const [cellDialog, setCellDialog] = useState(null);
  const [cellSaving, setCellSaving] = useState(false);
  const [rowVisRules, setRowVisRules] = useState({});
  const [rowVisChanged, setRowVisChanged] = useState(false);
  const [rowVisDialog, setRowVisDialog] = useState(null);
  const [mergedRanges, setMergedRanges] = useState([]);
  const [mergeInput, setMergeInput] = useState("");
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState("");
  const [replaceText, setReplaceText] = useState("");
  const [matchCase, setMatchCase] = useState(true);
  const [frResult, setFrResult] = useState(null);
  const [frRunning, setFrRunning] = useState(false);
  // Input map sub-mode
  const [imAction, setImAction] = useState("allow");
  // Selected row/col for operations
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedCol, setSelectedCol] = useState(null);
  const [opLoading, setOpLoading] = useState(false);
  const gridRef = useRef(null);
  const { handleResizeStart: handleColResize, getColStyle } = useColWidths(
    templateId ? `visual:${templateId}` : null, activeSheet
  );

  useEffect(() => { fetchUnits(); }, []);
  useEffect(() => { if (templateId) loadTemplateSheets(templateId); }, [templateId]);
  useEffect(() => { if (activeSheet && templateId) loadSheetCells(activeSheet); }, [activeSheet, templateId]);

  const loadTemplateSheets = async (tid) => {
    try {
      const res = await api.get(`/templates/${tid}`);
      setTemplateSheets(res.data.sheets || []);
      const imRes = await api.get(`/templates/${tid}/input-map`);
      setInputMap(imRes.data);
      const rvRes = await api.get(`/templates/${tid}/row-visibility`);
      setRowVisRules(rvRes.data.rules || {}); setRowVisChanged(false);
      setLocalOverrides({}); setHasChanges(false);
      if (res.data.sheets?.length > 0) setActiveSheet(res.data.sheets[0].sheet_name);
    } catch { toast.error(t("loadFailed")); }
  };

  const loadSheetCells = async (sheetName) => {
    setLoading(true); setSelectedRow(null); setSelectedCol(null);
    try {
      const res = await api.get(`/templates/${templateId}/sheets/${encodeURIComponent(sheetName)}/cells`);
      setCells(res.data.cells || []);
      const sheetData = templateSheets.find(s => s.sheet_name === sheetName);
      setMergedRanges(sheetData?.merged_cells || []);
      setMergeInput("");
    } catch { toast.error(t("loadFailed")); }
    finally { setLoading(false); }
  };

  const loadVersions = async () => {
    if (!templateId) return;
    try { const res = await api.get(`/templates/${templateId}/input-map/versions`); setVersions(res.data); setShowVersions(true); }
    catch { toast.error(t("loadFailed")); }
  };

  const loadDiff = async (from, to) => {
    try { const res = await api.get(`/templates/${templateId}/input-map/diff?from=${from}&to=${to}`); setDiff(res.data); }
    catch { toast.error(t("loadFailed")); }
  };

  const getCellStatus = useCallback((address) => {
    return localOverrides[activeSheet]?.[address] ?? null;
  }, [localOverrides, activeSheet]);

  const applyToCells = useCallback((startRow, startCol, endRow, endCol) => {
    const minR = Math.min(startRow, endRow), maxR = Math.max(startRow, endRow);
    const minC = Math.min(startCol, endCol), maxC = Math.max(startCol, endCol);
    const cellSet = new Set(cells.filter(c => c.is_formula).map(c => c.address));
    const newOverrides = { ...localOverrides };
    if (!newOverrides[activeSheet]) newOverrides[activeSheet] = {};
    for (let r = minR; r <= maxR; r++) {
      for (let c = minC; c <= maxC; c++) {
        const addr = cellToRef(r, c);
        if (cellSet.has(addr)) continue;
        newOverrides[activeSheet][addr] = imAction === "clear" ? null : imAction;
      }
    }
    setLocalOverrides(newOverrides);
    setHasChanges(true);
  }, [cells, activeSheet, imAction, localOverrides]);

  const handleMouseDown = (row, col, cell) => {
    if (editorMode !== "inputmap") return;
    if (cell?.is_formula) return;
    setIsDragging(true); setDragStart({ row, col }); setDragEnd({ row, col });
  };
  const handleMouseMove = (row, col) => { if (!isDragging) return; setDragEnd({ row, col }); };
  const handleMouseUp = () => {
    if (!isDragging || !dragStart || !dragEnd) { setIsDragging(false); return; }
    applyToCells(dragStart.row, dragStart.col, dragEnd.row, dragEnd.col);
    setIsDragging(false); setDragStart(null); setDragEnd(null);
  };

  // ─── Cell Click (edit mode: single click) ───
  const handleCellClick = (cell, row, col) => {
    if (editorMode === "edit") {
      handleCellEditOpen(cell, row, col);
    } else if (editorMode === "inputmap") {
      handleMouseDown(row, col, cell);
    }
  };

  const handleCellEditOpen = (cell, row, col) => {
    const addr = cell?.address || cellToRef(row, col);
    const style = cell?.style || {};
    setCellDialog({
      cell: cell || { address: addr, row, col, is_formula: false, default_value: null, formula: null, style: {} },
      value: cell?.default_value ?? cell?.computed_value ?? "",
      formula: cell?.formula ?? "",
      fontSize: style.fontSize || "",
      fontWeight: style.fontWeight || (style.bold ? "bold" : "regular"),
      align: style.align || "",
    });
  };

  const handleCellEditSave = async () => {
    if (!cellDialog || !templateId) return;
    setCellSaving(true);
    try {
      const val = cellDialog.value ?? "";
      const formula = cellDialog.formula || null;
      const styleUpdate = {};
      if (cellDialog.fontWeight === "bold") { styleUpdate.bold = true; styleUpdate.fontWeight = "bold"; }
      else if (cellDialog.fontWeight === "semibold") { styleUpdate.bold = false; styleUpdate.fontWeight = "semibold"; }
      else { styleUpdate.bold = false; styleUpdate.fontWeight = "regular"; }
      if (cellDialog.fontSize && !isNaN(cellDialog.fontSize)) styleUpdate.fontSize = parseFloat(cellDialog.fontSize);
      if (cellDialog.align) styleUpdate.align = cellDialog.align;
      await api.patch(`/templates/${templateId}/cells`, {
        sheet_name: activeSheet, address: cellDialog.cell.address,
        value: val, formula: formula, style: styleUpdate,
      });
      const isF = !!formula && formula.startsWith("=");
      const updatedCell = {
        ...cellDialog.cell, default_value: val, formula: formula, is_formula: isF,
        computed_value: val, style: { ...(cellDialog.cell.style || {}), ...styleUpdate },
      };
      setCells(prev => {
        const exists = prev.some(c => c.address === cellDialog.cell.address);
        if (exists) return prev.map(c => c.address === cellDialog.cell.address ? updatedCell : c);
        return [...prev, updatedCell];
      });
      setCellDialog(null);
      toast.success(`${cellDialog.cell.address} ${t("cellSaved")}`);
    } catch (err) { toast.error(err.response?.data?.detail || t("saveFailed")); }
    finally { setCellSaving(false); }
  };

  const handleCellDelete = async () => {
    if (!cellDialog || !templateId) return;
    setCellSaving(true);
    try {
      await api.patch(`/templates/${templateId}/cells`, {
        sheet_name: activeSheet, address: cellDialog.cell.address, delete: true,
      });
      setCells(prev => prev.filter(c => c.address !== cellDialog.cell.address));
      setCellDialog(null);
      toast.success(`${cellDialog.cell.address} ${t("cellDeleted")}`);
    } catch (err) { toast.error(err.response?.data?.detail || t("saveFailed")); }
    finally { setCellSaving(false); }
  };

  // ─── Row/Column Operations ───
  const handleRowOp = async (op) => {
    if (!selectedRow || !templateId || !activeSheet) return;
    setOpLoading(true);
    try {
      await api.post(`/templates/${templateId}/sheets/${encodeURIComponent(activeSheet)}/${op}`, {
        sheet_name: activeSheet, position: selectedRow, count: 1,
      });
      toast.success(`${op === "insert-row" ? "Ред добавен" : "Ред изтрит"} на позиция ${selectedRow}`);
      await loadSheetCells(activeSheet);
      // Reload template sheets to get updated merges
      const res = await api.get(`/templates/${templateId}`);
      setTemplateSheets(res.data.sheets || []);
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
    finally { setOpLoading(false); }
  };

  const handleColOp = async (op) => {
    if (!selectedCol || !templateId || !activeSheet) return;
    setOpLoading(true);
    try {
      await api.post(`/templates/${templateId}/sheets/${encodeURIComponent(activeSheet)}/${op}`, {
        sheet_name: activeSheet, position: selectedCol, count: 1,
      });
      toast.success(`${op === "insert-col" ? "Колона добавена" : "Колона изтрита"} на позиция ${selectedCol}`);
      await loadSheetCells(activeSheet);
      const res = await api.get(`/templates/${templateId}`);
      setTemplateSheets(res.data.sheets || []);
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
    finally { setOpLoading(false); }
  };

  // ─── Batch Alignment ───
  const handleBatchAlign = async (align, target) => {
    if (!templateId || !activeSheet) return;
    let addresses = [];
    const sheet = templateSheets.find(s => s.sheet_name === activeSheet);
    const maxRow = sheet?.max_row || Math.max(...cells.map(c => c.row), 0);
    const maxCol = sheet?.max_col || Math.max(...cells.map(c => c.col), 0);
    if (target === "row" && selectedRow) {
      for (let c = 1; c <= Math.max(maxCol, 20); c++) addresses.push(cellToRef(selectedRow, c));
    } else if (target === "col" && selectedCol) {
      for (let r = 1; r <= Math.max(maxRow, 60); r++) addresses.push(cellToRef(r, selectedCol));
    } else {
      return;
    }
    setOpLoading(true);
    try {
      await api.post(`/templates/${templateId}/sheets/${encodeURIComponent(activeSheet)}/batch-style`, {
        addresses, style: { align },
      });
      setCells(prev => prev.map(c => {
        const match = target === "row" ? c.row === selectedRow : c.col === selectedCol;
        return match ? { ...c, style: { ...(c.style || {}), align } } : c;
      }));
      const label = target === "row" ? `Ред ${selectedRow}` : `Колона ${getColLetter(selectedCol - 1)}`;
      toast.success(`${label}: подравняване ${align === "left" ? "ляво" : align === "center" ? "център" : "дясно"}`);
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
    finally { setOpLoading(false); }
  };

  // ─── Publishing & Saving ───
  const handlePublishCellChanges = async () => {
    if (!templateId) return;
    setPublishing(true);
    try {
      const res = await api.post(`/templates/${templateId}/publish-cell-changes`, {
        target_statuses: ["DRAFT", "SUBMITTED", "NEEDS_CORRECTION"],
      });
      const d = res.data;
      if (d.errors?.length > 0) {
        toast.error(`Грешки: ${d.errors[0]}`);
      } else {
        toast.success(`Публикувано: ${d.cells_updated} клетки обновени в ${d.instances} отчета${d.cells_deleted > 0 ? `, ${d.cells_deleted} изтрити` : ""}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
    finally { setPublishing(false); }
  };

  const handleMergeCells = async () => {
    if (!mergeInput || !templateId || !activeSheet) return;
    const range = mergeInput.trim().toUpperCase();
    if (!range.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/)) { toast.error("Невалиден формат. Пример: A1:G1"); return; }
    try {
      await api.post(`/templates/${templateId}/merge-cells`, { sheet_name: activeSheet, range });
      setMergedRanges(prev => [...prev, range]);
      setMergeInput("");
      toast.success(`${t("cellsMerged")}: ${range}`);
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
  };

  const handleUnmergeCells = async (range) => {
    if (!templateId || !activeSheet) return;
    try {
      await api.post(`/templates/${templateId}/unmerge-cells`, { sheet_name: activeSheet, range });
      setMergedRanges(prev => prev.filter(r => r !== range));
      toast.success(`${t("cellsUnmerged")}: ${range}`);
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
  };

  const handleFindReplace = async () => {
    if (!findText || !templateId) return;
    setFrRunning(true); setFrResult(null);
    try {
      const res = await api.post(`/templates/${templateId}/find-replace`, {
        find: findText, replace: replaceText,
        sheet_name: activeSheet || null, match_case: matchCase,
      });
      setFrResult(res.data);
      if (res.data.count > 0) { toast.success(`${res.data.count} ${t("replacementsMade")}`); if (activeSheet) loadSheetCells(activeSheet); }
      else toast.info(t("noMatchesFound"));
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
    finally { setFrRunning(false); }
  };

  const handleRowClick = (row) => { if (editorMode === "rowvis") setRowVisDialog({ row }); };
  const assignRowUnit = (row, unitCode) => {
    setRowVisRules(prev => {
      const next = { ...prev };
      if (!next[activeSheet]) next[activeSheet] = {};
      else next[activeSheet] = { ...next[activeSheet] };
      if (unitCode) next[activeSheet][String(row)] = unitCode;
      else delete next[activeSheet][String(row)];
      if (Object.keys(next[activeSheet]).length === 0) delete next[activeSheet];
      return next;
    });
    setRowVisChanged(true); setRowVisDialog(null);
  };
  const saveRowVisibility = async () => {
    if (!templateId) return; setCellSaving(true);
    try { await api.put(`/templates/${templateId}/row-visibility`, { rules: rowVisRules }); toast.success(t("rowVisibilitySaved")); setRowVisChanged(false); }
    catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
    finally { setCellSaving(false); }
  };
  const getRowUnitCode = (row) => rowVisRules[activeSheet]?.[String(row)] || null;

  const buildUpdatedInputMap = () => {
    if (!inputMap) return null;
    const newMap = { deny_sheets: [...(inputMap.deny_sheets || [])], allow_ranges: { ...(inputMap.allow_ranges || {}) }, deny_ranges: { ...(inputMap.deny_ranges || {}) }, allow_cells: { ...(inputMap.allow_cells || {}) }, deny_cells: { ...(inputMap.deny_cells || {}) }, notes: { ...(inputMap.notes || {}) } };
    for (const [sheetName, overrides] of Object.entries(localOverrides)) {
      const allowCells = new Set(newMap.allow_cells[sheetName] || []);
      const denyCells = new Set(newMap.deny_cells[sheetName] || []);
      for (const [addr, action] of Object.entries(overrides)) {
        if (action === "allow") { allowCells.add(addr); denyCells.delete(addr); }
        else if (action === "deny") { denyCells.add(addr); allowCells.delete(addr); }
        else { allowCells.delete(addr); denyCells.delete(addr); }
      }
      if (allowCells.size > 0) newMap.allow_cells[sheetName] = [...allowCells]; else delete newMap.allow_cells[sheetName];
      if (denyCells.size > 0) newMap.deny_cells[sheetName] = [...denyCells]; else delete newMap.deny_cells[sheetName];
    }
    return newMap;
  };

  const handlePublish = async () => {
    const updatedMap = hasChanges ? buildUpdatedInputMap() : null;
    setPublishing(true);
    try {
      const res = await api.post(`/templates/${templateId}/input-map/publish`, { input_map: updatedMap, target_statuses: ["DRAFT", "SUBMITTED", "NEEDS_CORRECTION"] });
      setPublishResult(res.data); setShowPublishResult(true);
      if (hasChanges && updatedMap) setInputMap({ ...updatedMap, version: res.data.new_version });
      else if (inputMap) setInputMap({ ...inputMap, version: res.data.new_version });
      setLocalOverrides({}); setHasChanges(false);
      if (activeSheet) loadSheetCells(activeSheet);
      if (showVersions) loadVersions();
    } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
    finally { setPublishing(false); }
  };

  const handleSaveOnly = async () => {
    const updated = buildUpdatedInputMap();
    if (!updated) return; setPublishing(true);
    try {
      const res = await api.put(`/templates/${templateId}/input-map`, updated);
      toast.success(`${t("saved")} — ${res.data.editable_count} ${t("editableCells")} (v${res.data.version})`);
      setInputMap({ ...updated, version: res.data.version }); setLocalOverrides({}); setHasChanges(false);
      if (activeSheet) loadSheetCells(activeSheet);
    } catch (e) { toast.error(e.response?.data?.detail || t("saveFailed")); }
    finally { setPublishing(false); }
  };

  const isInDragRange = (row, col) => {
    if (!isDragging || !dragStart || !dragEnd) return false;
    const minR = Math.min(dragStart.row, dragEnd.row), maxR = Math.max(dragStart.row, dragEnd.row);
    const minC = Math.min(dragStart.col, dragEnd.col), maxC = Math.max(dragStart.col, dragEnd.col);
    return row >= minR && row <= maxR && col >= minC && col <= maxC;
  };

  const maxRow = cells.length > 0 ? Math.max(...cells.map(c => c.row)) : 0;
  const maxCol = cells.length > 0 ? Math.max(...cells.map(c => c.col)) : 0;
  const gridRows = maxRow + EXTRA_ROWS;
  const gridCols = maxCol + EXTRA_COLS;
  const cellMap = {}; cells.forEach(c => { cellMap[`${c.row},${c.col}`] = c; });
  const isDeniedSheet = inputMap?.deny_sheets?.includes(activeSheet);
  const sheetAllowCount = cells.filter(c => { const ov = getCellStatus(c.address); if (ov === "allow") return true; if (ov === "deny" || (ov === null && !c.is_input)) return false; return c.is_input; }).length;

  // Merge color map
  const mergeColorMap = {};
  const mergeColors = ["#fef3c7","#dbeafe","#ede9fe","#fce7f3","#d1fae5","#fee2e2","#e0e7ff","#fef9c3","#ccfbf1","#f3e8ff"];
  mergedRanges.forEach((rangeStr, idx) => {
    const m = rangeStr.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/);
    if (!m) return;
    const c1 = colFromLetter(m[1]), r1 = parseInt(m[2]), c2 = colFromLetter(m[3]), r2 = parseInt(m[4]);
    const color = mergeColors[idx % mergeColors.length];
    for (let r = r1; r <= r2; r++)
      for (let c = c1; c <= c2; c++)
        mergeColorMap[`${r},${c}`] = { color, range: rangeStr, isTopLeft: r === r1 && c === c1 };
  });

  return (
    <div className="space-y-4">
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="py-3 px-5 border-b border-slate-100">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Grid3X3 className="w-4 h-4 text-[#14532d] shrink-0" />
              <div className="w-56">
                <Select value={templateId || ""} onValueChange={setTemplateId}>
                  <SelectTrigger data-testid="visual-template-select" className="h-8 text-xs"><SelectValue placeholder={t("chooseTemplate")} /></SelectTrigger>
                  <SelectContent>{templates.filter(tp => !tp.is_deleted).map(tp => (<SelectItem key={tp.id} value={tp.id}>{tp.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>

            {templateId && (
              <div className="flex items-center gap-1.5">
                {/* Mode switcher */}
                <div className="flex rounded-lg border border-slate-200 overflow-hidden mr-2">
                  {[
                    { id: "edit", icon: Pencil, label: "Редактиране", color: "blue" },
                    { id: "inputmap", icon: Grid3X3, label: "Входна карта", color: "emerald" },
                    { id: "rowvis", icon: Eye, label: "Видимост", color: "teal" },
                  ].map(({ id, icon: Icon, label, color }) => (
                    <button key={id} data-testid={`mode-${id}`}
                      onClick={() => setEditorMode(id)}
                      className={`flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium transition-colors ${
                        editorMode === id
                          ? `bg-${color}-600 text-white`
                          : `text-slate-500 hover:bg-slate-50`
                      }`}
                      style={editorMode === id ? { backgroundColor: color === "blue" ? "#2563eb" : color === "emerald" ? "#059669" : "#0d9488", color: "white" } : {}}>
                      <Icon className="w-3.5 h-3.5" /> {label}
                    </button>
                  ))}
                </div>

                <Button data-testid="toggle-find-replace" size="sm" variant={showFindReplace ? "default" : "outline"}
                  onClick={() => setShowFindReplace(!showFindReplace)}
                  className={`h-8 text-[11px] ${showFindReplace ? "bg-orange-600 hover:bg-orange-700 text-white" : "text-orange-600 border-orange-200"}`}>
                  <Search className="w-3.5 h-3.5 mr-1" /> Търси
                </Button>

                <div className="w-px h-6 bg-slate-200" />

                {rowVisChanged && <Button size="sm" onClick={saveRowVisibility} disabled={cellSaving} className="h-8 text-[11px] bg-teal-600 hover:bg-teal-700 text-white"><Save className="w-3.5 h-3.5 mr-1" /> {t("save")}</Button>}
                {hasChanges && <Button size="sm" variant="outline" onClick={handleSaveOnly} disabled={publishing} className="h-8 text-[11px]"><Save className="w-3.5 h-3.5 mr-1" /> {t("save")}</Button>}
                <Button data-testid="publish-input-map" size="sm" onClick={handlePublish} disabled={publishing} className="h-8 text-[11px] bg-[#14532d] hover:bg-[#166534] text-white">
                  {publishing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} {t("publish")}
                </Button>
                <Button data-testid="publish-cell-changes" size="sm" variant="outline" onClick={handlePublishCellChanges} disabled={publishing} className="h-8 text-[11px] text-blue-600 border-blue-200">
                  <Send className="w-3.5 h-3.5 mr-1" /> {t("publishCellChanges")}
                </Button>
                <Button data-testid="show-versions" variant="ghost" size="sm" onClick={loadVersions} className="h-8 text-[11px]"><History className="w-3.5 h-3.5" /></Button>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* Sheet tabs */}
          {templateSheets.length > 0 && (
            <div className="flex items-center gap-0.5 px-3 py-1.5 bg-slate-50 border-b border-slate-200 overflow-x-auto">
              {templateSheets.map(s => {
                const isDenied = inputMap?.deny_sheets?.includes(s.sheet_name);
                return (<button key={s.sheet_name} data-testid={`ve-sheet-${s.order_index}`}
                  onClick={() => setActiveSheet(s.sheet_name)}
                  className={`px-3 py-1 text-[11px] font-medium whitespace-nowrap rounded transition-colors ${
                    activeSheet === s.sheet_name ? "bg-[#14532d] text-white shadow-sm" : isDenied ? "bg-red-50 text-red-500 border border-red-200" : "text-slate-500 hover:bg-white hover:text-slate-700"
                  }`}>
                  {s.sheet_name}
                </button>);
              })}
            </div>
          )}

          {/* Context toolbar (changes based on mode) */}
          {templateId && activeSheet && (
            <div className="flex flex-wrap items-center gap-y-1 gap-x-3 px-3 py-1.5 bg-white border-b border-slate-100">
              {editorMode === "edit" && (
                <>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-400 mr-1">Редове/Колони:</span>
                    <Button data-testid="insert-row-above" size="sm" variant="outline" disabled={!selectedRow || opLoading}
                      onClick={() => handleRowOp("insert-row")}
                      className="h-6 text-[10px] px-1.5 text-blue-600 border-blue-200 hover:bg-blue-50">
                      <Plus className="w-3 h-3 mr-0.5" /> Ред горе
                    </Button>
                    <Button data-testid="insert-row-below" size="sm" variant="outline" disabled={!selectedRow || opLoading}
                      onClick={async () => {
                        if (!selectedRow || !templateId || !activeSheet) return;
                        setOpLoading(true);
                        try {
                          await api.post(`/templates/${templateId}/sheets/${encodeURIComponent(activeSheet)}/insert-row`, {
                            sheet_name: activeSheet, position: selectedRow + 1, count: 1,
                          });
                          toast.success(`Ред добавен на позиция ${selectedRow + 1}`);
                          await loadSheetCells(activeSheet);
                          const res = await api.get(`/templates/${templateId}`);
                          setTemplateSheets(res.data.sheets || []);
                        } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
                        finally { setOpLoading(false); }
                      }}
                      className="h-6 text-[10px] px-1.5 text-blue-600 border-blue-200 hover:bg-blue-50">
                      <Plus className="w-3 h-3 mr-0.5" /> Ред долу
                    </Button>
                    <Button data-testid="delete-row" size="sm" variant="outline" disabled={!selectedRow || opLoading}
                      onClick={() => handleRowOp("delete-row")}
                      className="h-6 text-[10px] px-1.5 text-red-500 border-red-200 hover:bg-red-50">
                      <Trash2 className="w-3 h-3 mr-0.5" /> Ред
                    </Button>
                    <div className="w-px h-4 bg-slate-200 mx-1" />
                    <Button data-testid="insert-col-left" size="sm" variant="outline" disabled={!selectedCol || opLoading}
                      onClick={() => handleColOp("insert-col")}
                      className="h-6 text-[10px] px-1.5 text-blue-600 border-blue-200 hover:bg-blue-50">
                      <Plus className="w-3 h-3 mr-0.5" /> Колона ляво
                    </Button>
                    <Button data-testid="insert-col-right" size="sm" variant="outline" disabled={!selectedCol || opLoading}
                      onClick={async () => {
                        if (!selectedCol || !templateId || !activeSheet) return;
                        setOpLoading(true);
                        try {
                          await api.post(`/templates/${templateId}/sheets/${encodeURIComponent(activeSheet)}/insert-col`, {
                            sheet_name: activeSheet, position: selectedCol + 1, count: 1,
                          });
                          toast.success(`Колона добавена на позиция ${selectedCol + 1}`);
                          await loadSheetCells(activeSheet);
                          const res = await api.get(`/templates/${templateId}`);
                          setTemplateSheets(res.data.sheets || []);
                        } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
                        finally { setOpLoading(false); }
                      }}
                      className="h-6 text-[10px] px-1.5 text-blue-600 border-blue-200 hover:bg-blue-50">
                      <Plus className="w-3 h-3 mr-0.5" /> Колона дясно
                    </Button>
                    <Button data-testid="delete-col" size="sm" variant="outline" disabled={!selectedCol || opLoading}
                      onClick={() => handleColOp("delete-col")}
                      className="h-6 text-[10px] px-1.5 text-red-500 border-red-200 hover:bg-red-50">
                      <Trash2 className="w-3 h-3 mr-0.5" /> Колона
                    </Button>
                    {opLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500 ml-1" />}
                  </div>
                  {(selectedRow || selectedCol) && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-slate-400 mr-1">
                        Подравни {selectedRow && selectedCol ? "клетка" : selectedRow ? "ред" : "колона"}:
                      </span>
                      {[["left", AlignLeft, "Ляво"], ["center", AlignCenter, "Център"], ["right", AlignRight, "Дясно"]].map(([val, Icon, label]) => (
                        <Button key={val} data-testid={`align-${val}`} size="sm" variant="outline" disabled={opLoading}
                          onClick={async () => {
                            if (selectedRow && selectedCol) {
                              // Single cell — use the cell edit endpoint
                              const addr = cellToRef(selectedRow, selectedCol);
                              setOpLoading(true);
                              try {
                                await api.post(`/templates/${templateId}/sheets/${encodeURIComponent(activeSheet)}/batch-style`, {
                                  addresses: [addr], style: { align: val },
                                });
                                setCells(prev => prev.map(c => c.row === selectedRow && c.col === selectedCol
                                  ? { ...c, style: { ...(c.style || {}), align: val } } : c));
                                toast.success(`${addr}: подравняване ${label.toLowerCase()}`);
                              } catch (e) { toast.error(e.response?.data?.detail || t("failed")); }
                              finally { setOpLoading(false); }
                            } else {
                              handleBatchAlign(val, selectedRow ? "row" : "col");
                            }
                          }}
                          className="h-6 text-[10px] px-1.5 text-violet-600 border-violet-200 hover:bg-violet-50">
                          <Icon className="w-3 h-3 mr-0.5" /> {label}
                        </Button>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-[10px] text-slate-400">
                    {selectedRow && <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">Ред: {selectedRow}</span>}
                    {selectedCol && <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">Кол: {getColLetter(selectedCol - 1)}</span>}
                    <span>Кликнете за редактиране</span>
                  </div>
                </>
              )}
              {editorMode === "inputmap" && (
                <>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-400 mr-1">{t("actions")}:</span>
                    <Button data-testid="mode-allow" variant={imAction === "allow" ? "default" : "outline"} size="sm" onClick={() => setImAction("allow")}
                      className={`h-6 text-[10px] ${imAction === "allow" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}><Check className="w-3 h-3 mr-0.5" /> {t("allow")}</Button>
                    <Button data-testid="mode-deny" variant={imAction === "deny" ? "default" : "outline"} size="sm" onClick={() => setImAction("deny")}
                      className={`h-6 text-[10px] ${imAction === "deny" ? "bg-red-600 hover:bg-red-700 text-white" : ""}`}><X className="w-3 h-3 mr-0.5" /> {t("deny")}</Button>
                    <Button data-testid="mode-clear" variant={imAction === "clear" ? "default" : "outline"} size="sm" onClick={() => setImAction("clear")}
                      className={`h-6 text-[10px] ${imAction === "clear" ? "bg-slate-600 hover:bg-slate-700 text-white" : ""}`}><Eraser className="w-3 h-3 mr-0.5" /> {t("clear")}</Button>
                    <div className="w-px h-4 bg-slate-200 mx-1" />
                    <Input data-testid="merge-range-input" placeholder="A1:G1" className="h-6 w-24 text-[10px] font-mono" value={mergeInput}
                      onChange={e => setMergeInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleMergeCells(); }} />
                    <Button data-testid="merge-btn" size="sm" variant="outline" className="h-6 text-[10px]" disabled={!mergeInput.trim()} onClick={handleMergeCells}>{t("merge")}</Button>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-slate-400">
                    <span className="flex items-center gap-0.5"><span className="w-2.5 h-2.5 rounded border border-emerald-300 bg-emerald-100" /> {t("allowed")}</span>
                    <span className="flex items-center gap-0.5"><span className="w-2.5 h-2.5 rounded border border-red-300 bg-red-100" /> {t("denied")}</span>
                    <span className="flex items-center gap-0.5"><span className="w-2.5 h-2.5 rounded border border-slate-200 bg-slate-100" /> {t("formula")}</span>
                    {isDeniedSheet && <span className="text-red-600 font-medium">{t("deniedSheet")}</span>}
                    <span className="text-slate-600 font-medium">{sheetAllowCount} {t("allowed")}</span>
                    {inputMap?.version && <span>v{inputMap.version}</span>}
                  </div>
                </>
              )}
              {editorMode === "rowvis" && (
                <div className="flex items-center gap-2">
                  <Eye className="w-3.5 h-3.5 text-teal-600" />
                  <span className="text-[11px] text-teal-700">Кликнете върху номер на ред за да зададете видимост по звено</span>
                </div>
              )}
            </div>
          )}

          {/* Merged ranges bar (inputmap mode) */}
          {editorMode === "inputmap" && mergedRanges.length > 0 && (
            <div className="bg-amber-50/50 px-3 py-1.5 border-b border-amber-200 max-h-[50px] overflow-y-auto">
              <div className="flex flex-wrap gap-1">
                {mergedRanges.map(r => (
                  <button key={r} onClick={() => handleUnmergeCells(r)}
                    className="px-1.5 py-0.5 text-[10px] font-mono rounded border border-amber-300 bg-white text-amber-800 hover:bg-red-50 hover:text-red-700 transition-colors"
                    title={t("clickToUnmerge")}>
                    {r} <span className="text-red-400">&times;</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Find & Replace Panel */}
          {showFindReplace && templateId && (
            <div className="border-b border-orange-200 bg-orange-50/50 px-4 py-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Input data-testid="find-input" value={findText} onChange={e => setFindText(e.target.value)}
                  className="h-7 text-xs flex-1 min-w-[120px]" placeholder="Търси..."
                  onKeyDown={e => { if (e.key === "Enter") handleFindReplace(); }} />
                <Input data-testid="replace-input" value={replaceText} onChange={e => setReplaceText(e.target.value)}
                  className="h-7 text-xs flex-1 min-w-[120px]" placeholder="Замени с..." />
                <label className="flex items-center gap-1 text-[10px] text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={matchCase} onChange={e => setMatchCase(e.target.checked)} className="rounded border-slate-300" />
                  Aa
                </label>
                <Button data-testid="find-replace-btn" size="sm" onClick={handleFindReplace} disabled={frRunning || !findText}
                  className="h-7 text-[10px] bg-orange-600 hover:bg-orange-700 text-white">
                  {frRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Replace className="w-3.5 h-3.5 mr-0.5" />} Замени всички
                </Button>
              </div>
            </div>
          )}

          {/* GRID */}
          {templateId && activeSheet && (
            <div data-testid="visual-editor-grid" className="overflow-auto max-h-[550px]"
              ref={gridRef} onMouseUp={handleMouseUp} onMouseLeave={() => { if (isDragging) handleMouseUp(); }} style={{ userSelect: "none" }}>
              {loading ? <div className="flex items-center justify-center h-40"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div> : (
                <table className="border-collapse text-[11px]" style={{ fontFamily: "'Calibri', 'Segoe UI', sans-serif", tableLayout: "fixed" }}>
                  <thead className="sticky top-0 z-10"><tr>
                    <th className="bg-slate-200 border border-slate-300 px-1 py-1 text-center text-slate-500 font-semibold w-8 min-w-[32px]">#</th>
                    {Array.from({ length: gridCols }, (_, i) => (
                      <th key={i}
                        onClick={() => { setSelectedCol(i + 1); setSelectedRow(null); }}
                        className={`border border-slate-300 px-1 py-1 text-center font-semibold relative select-none cursor-pointer transition-colors ${
                          selectedCol === i + 1 ? "bg-blue-200 text-blue-800" : "bg-slate-200 text-slate-600 hover:bg-slate-300"
                        }`}
                        style={{ fontSize: "10px", ...getColStyle(i) }}>
                        {getColLetter(i)}
                        <div onMouseDown={(e) => { e.stopPropagation(); handleColResize(i, e); }}
                          className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize hover:bg-slate-400/40 active:bg-slate-400/60"
                          style={{ touchAction: "none" }} />
                      </th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {Array.from({ length: gridRows }, (_, rowIdx) => {
                      const row = rowIdx + 1;
                      return (<tr key={row}>
                        <td
                          onClick={() => { setSelectedRow(row); setSelectedCol(null); if (editorMode === "rowvis") handleRowClick(row); }}
                          className={`border border-slate-300 px-1 py-0 text-center font-normal cursor-pointer transition-colors ${
                            selectedRow === row ? "bg-blue-200 text-blue-800 font-semibold" :
                            getRowUnitCode(row) ? "bg-teal-50 text-teal-700 font-medium" :
                            "bg-slate-100 text-slate-400 hover:bg-slate-200"
                          }`}
                          style={{ fontSize: "9px", minWidth: getRowUnitCode(row) ? "55px" : "32px" }}
                          title={getRowUnitCode(row) ? `${t("assignUnit")}: ${getRowUnitCode(row)}` : `Ред ${row}`}>
                          {getRowUnitCode(row) ? <span>{row}<span className="text-[6px] ml-0.5">{getRowUnitCode(row)}</span></span> : row}
                        </td>
                        {Array.from({ length: gridCols }, (_, colIdx) => {
                          const col = colIdx + 1;
                          const cell = cellMap[`${row},${col}`];
                          const addr = cellToRef(row, col);
                          const isInDrag = isInDragRange(row, col);
                          const override = getCellStatus(addr);
                          const mergeInfo = mergeColorMap[`${row},${col}`];
                          const isEmpty = !cell;
                          let bgColor = isEmpty ? "#fafafa" : "#ffffff";
                          let borderColor = "#e2e8f0";
                          let textColor = "#475569";

                          if (editorMode === "inputmap") {
                            if (mergeInfo) { bgColor = mergeInfo.color; borderColor = "#f59e0b"; }
                            else if (cell?.is_formula) { bgColor = "#f1f5f9"; textColor = "#94a3b8"; }
                            else if (isDeniedSheet) { bgColor = "#fef2f2"; }
                            else if (override === "allow") { bgColor = "#d1fae5"; borderColor = "#6ee7b7"; }
                            else if (override === "deny") { bgColor = "#fecaca"; borderColor = "#f87171"; }
                            else if (cell?.is_input) { bgColor = "#ecfdf5"; borderColor = "#a7f3d0"; }
                            if (isInDrag && !cell?.is_formula) bgColor = imAction === "allow" ? "#a7f3d0" : imAction === "deny" ? "#fca5a5" : "#e2e8f0";
                          } else if (editorMode === "edit") {
                            if (cell?.is_formula) { bgColor = "#f1f5f9"; textColor = "#94a3b8"; }
                            else if (cell?.style?.bold) textColor = "#0f172a";
                            if (mergeInfo) { bgColor = mergeInfo.color; borderColor = "#f59e0b"; }
                          }

                          let displayVal = cell?.default_value ?? "";
                          if (displayVal === "None") displayVal = "";
                          const truncVal = displayVal.length > 10 ? displayVal.substring(0, 10) + "\u2026" : displayVal;

                          return (<td key={col} data-testid={`ve-cell-${addr}`}
                            onClick={() => { setSelectedRow(row); setSelectedCol(col); handleCellClick(cell, row, col); }}
                            onMouseDown={() => handleMouseDown(row, col, cell)}
                            onMouseMove={() => handleMouseMove(row, col)}
                            className={`border px-0.5 py-0 relative ${
                              editorMode === "edit" ? "cursor-pointer hover:ring-1 hover:ring-blue-300 hover:ring-inset" :
                              editorMode === "inputmap" ? "cursor-crosshair" : ""
                            }`}
                            style={{
                              backgroundColor: bgColor, borderColor, color: textColor,
                              minWidth: "48px", maxWidth: "100px", height: "22px",
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: "10px",
                              fontWeight: cell?.style?.fontWeight === "bold" || cell?.style?.bold ? "700" : cell?.style?.fontWeight === "semibold" ? "600" : "normal",
                              textAlign: cell?.style?.align || undefined,
                              ...getColStyle(colIdx),
                            }}>
                            {truncVal}
                            {cell?.is_formula && <FunctionSquare className="w-2 h-2 absolute top-0 right-0 text-slate-300" />}
                            {mergeInfo?.isTopLeft && <span className="absolute bottom-0 right-0 text-[6px] text-amber-500 px-0.5">M</span>}
                          </td>);
                        })}
                      </tr>);
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {!templateId && (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <MousePointerClick className="w-10 h-10 text-slate-300 mb-3" />
              <p className="text-sm">{t("selectTemplate")}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cell Edit Dialog */}
      <Dialog open={!!cellDialog} onOpenChange={open => { if (!open) setCellDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Pencil className="w-4 h-4 text-[#14532d]" />
              <span className="font-mono">{activeSheet}!{cellDialog?.cell?.address}</span>
            </DialogTitle>
          </DialogHeader>
          {cellDialog && (
            <div className="space-y-3 pt-1">
              {cellDialog.cell.is_formula && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
                  <FunctionSquare className="w-3 h-3 mr-1" /> {t("formula")}
                </span>
              )}
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-700">{t("cellValue")}</Label>
                <Input data-testid="ve-edit-value" value={cellDialog.value}
                  onChange={e => setCellDialog(prev => ({ ...prev, value: e.target.value }))}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleCellEditSave(); } }}
                  className="h-8 text-sm font-mono" placeholder="..." autoFocus />
                <div className="flex items-center gap-1 pt-0.5">
                  <span className="text-[9px] text-slate-400">Тагове:</span>
                  {["{ДИРЕКТОР}", "{СЧЕТОВОДИТЕЛ}", "{ЗВЕНО}"].map(tag => (
                    <button key={tag} type="button"
                      onClick={() => setCellDialog(prev => ({ ...prev, value: (prev.value || "") + tag }))}
                      className="px-1 py-0 text-[9px] font-mono rounded border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-700">
                  <FunctionSquare className="w-3 h-3 inline mr-1" />{t("formulaLabel")}
                </Label>
                <Input data-testid="ve-edit-formula" value={cellDialog.formula}
                  onChange={e => setCellDialog(prev => ({ ...prev, formula: e.target.value }))}
                  className="h-8 text-sm font-mono" placeholder='=SUM(A1:A10)' />
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 space-y-0.5">
                  <Label className="text-[10px] text-slate-500">{t("fontWeight")}</Label>
                  <div className="flex rounded-md border border-slate-200 overflow-hidden">
                    {[["regular", "R"], ["semibold", "S"], ["bold", "B"]].map(([val, label]) => (
                      <button key={val} type="button" data-testid={`fw-${val}`}
                        onClick={() => setCellDialog(prev => ({ ...prev, fontWeight: val }))}
                        className={`flex-1 px-2 py-1 text-[10px] transition-colors ${cellDialog.fontWeight === val ? "bg-[#14532d] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                        style={{ fontWeight: val === "bold" ? 700 : val === "semibold" ? 600 : 400 }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="w-16 space-y-0.5">
                  <Label className="text-[10px] text-slate-500">Размер</Label>
                  <Input data-testid="ve-font-size" type="number" min="6" max="24" step="1"
                    value={cellDialog.fontSize} onChange={e => setCellDialog(prev => ({ ...prev, fontSize: e.target.value }))}
                    className="h-7 text-xs" placeholder="11" />
                </div>
                <div className="flex-1 space-y-0.5">
                  <Label className="text-[10px] text-slate-500">{t("textAlign")}</Label>
                  <div className="flex rounded-md border border-slate-200 overflow-hidden">
                    {[["", "A"], ["left", "L"], ["center", "C"], ["right", "R"]].map(([val, label]) => (
                      <button key={val} type="button"
                        onClick={() => setCellDialog(prev => ({ ...prev, align: val }))}
                        className={`flex-1 px-2 py-1 text-[10px] transition-colors ${cellDialog.align === val ? "bg-[#14532d] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex justify-between">
            <Button data-testid="ve-edit-delete" variant="outline" size="sm" onClick={handleCellDelete} disabled={cellSaving}
              className="text-red-500 border-red-200 hover:bg-red-50 mr-auto">{t("deleteCell")}</Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setCellDialog(null)}>{t("close")}</Button>
              <Button data-testid="ve-edit-save" size="sm" onClick={handleCellEditSave} disabled={cellSaving}
                className="bg-[#14532d] hover:bg-[#166534] text-white">
                {cellSaving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} {t("save")}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Row Visibility Dialog */}
      <Dialog open={!!rowVisDialog} onOpenChange={open => { if (!open) setRowVisDialog(null); }}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Eye className="w-4 h-4 text-teal-600" /> {t("assignUnit")} — {t("row")} {rowVisDialog?.row}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 pt-2 max-h-[300px] overflow-y-auto">
            <Button variant="outline" size="sm" className="w-full justify-start h-7 text-xs"
              onClick={() => assignRowUnit(rowVisDialog?.row, null)}>{t("allUnits")}</Button>
            {units.filter(u => u.code).map(u => (
              <Button key={u.id} variant={getRowUnitCode(rowVisDialog?.row) === u.code ? "default" : "outline"}
                size="sm" className={`w-full justify-start h-7 text-xs ${getRowUnitCode(rowVisDialog?.row) === u.code ? "bg-teal-600 text-white" : ""}`}
                onClick={() => assignRowUnit(rowVisDialog?.row, u.code)}>
                {u.code} — {u.name}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Publish Result Dialog */}
      <Dialog open={showPublishResult} onOpenChange={setShowPublishResult}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {publishResult?.warnings?.length > 0 && publishResult?.instances_updated === 0
                ? <><AlertTriangle className="w-5 h-5 text-amber-500" /> Публикуване</>
                : <><CheckCircle2 className="w-5 h-5 text-green-600" /> Публикувано успешно</>}
            </DialogTitle>
          </DialogHeader>
          {publishResult && (
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-slate-50 rounded p-2 text-center">
                  <p className="text-[9px] text-slate-400 uppercase">Версия</p>
                  <p className="text-base font-semibold text-slate-700">v{publishResult.old_version} &rarr; v{publishResult.new_version}</p>
                </div>
                <div className="bg-blue-50 rounded p-2 text-center">
                  <p className="text-[9px] text-blue-400 uppercase">Засегнати отчети</p>
                  <p data-testid="publish-instances-count" className="text-base font-semibold text-blue-700">{publishResult.instances_updated}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-green-50 rounded p-2 text-center"><Unlock className="w-4 h-4 text-green-600 mx-auto mb-0.5" /><p className="text-[9px] text-green-500 uppercase">Отключени</p><p className="text-sm font-semibold text-green-700">{publishResult.unlocked_cells}</p></div>
                <div className="bg-red-50 rounded p-2 text-center"><Lock className="w-4 h-4 text-red-500 mx-auto mb-0.5" /><p className="text-[9px] text-red-400 uppercase">Заключени</p><p className="text-sm font-semibold text-red-700">{publishResult.locked_cells}</p></div>
                <div className="bg-slate-50 rounded p-2 text-center"><p className="text-[9px] text-slate-400 uppercase">Общо</p><p className="text-sm font-semibold text-[#14532d]">{publishResult.editable_total}</p></div>
              </div>
              {publishResult.warnings?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2">
                  {publishResult.warnings.map((w, i) => <p key={i} className="text-xs text-amber-700">{w}</p>)}
                </div>
              )}
              <Button className="w-full" onClick={() => setShowPublishResult(false)}>{t("close")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Version History */}
      {showVersions && (
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="py-3 px-5 border-b border-slate-100 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2"><History className="w-4 h-4 text-[#14532d]" /> {t("versionHistory")}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => { setShowVersions(false); setDiff(null); }}>{t("close")}</Button>
          </CardHeader>
          <CardContent className="p-4">
            {versions.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">{t("noVersions")}</p> : (
              <div className="space-y-3">
                <div className="divide-y divide-slate-100 border border-slate-100 rounded-md">
                  {versions.map((v, idx) => (
                    <div key={v.id} className="flex items-center justify-between py-2 px-4 hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-mono font-semibold text-[#14532d] bg-emerald-50 px-2 py-0.5 rounded">v{v.version}</span>
                        <div><p className="text-xs text-slate-700">{v.updated_by_name || v.updated_by_email}</p><p className="text-[10px] text-slate-400">{new Date(v.updated_at).toLocaleString("bg-BG")}</p></div>
                      </div>
                      {idx < versions.length - 1 && (
                        <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => loadDiff(versions[idx + 1].version, v.version)}>
                          v{versions[idx + 1].version} <ArrowRight className="w-3 h-3 mx-0.5" /> v{v.version}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                {diff && (
                  <div className="border border-blue-100 rounded p-3 bg-blue-50/30">
                    <h4 className="text-xs font-semibold text-slate-700 mb-2">v{diff.from_version} → v{diff.to_version}</h4>
                    {diff.changes.length === 0 ? <p className="text-xs text-muted-foreground">{t("noChangesDetected")}</p> : (
                      <div className="space-y-1">
                        {diff.changes.map((ch, i) => (
                          <div key={i} className="text-xs bg-white rounded border border-slate-100 p-2">
                            <span className="font-medium text-slate-600">{ch.field} {ch.sheet && <span className="text-slate-400">[{ch.sheet}]</span>}</span>
                            {ch.added?.length > 0 && <div className="text-green-700 font-mono text-[10px]">+ {ch.added.join(", ")}</div>}
                            {ch.removed?.length > 0 && <div className="text-red-600 font-mono text-[10px]">- {ch.removed.join(", ")}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
